import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CameraIcon,
  PhotoIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  ShippingReportImage,
  ShippingReportImageType,
} from "../../../types/shippingReports";

type Props = {
  title: string;
  description: string;
  type: ShippingReportImageType;
  images: ShippingReportImage[];
  uploading: boolean;
  disabled?: boolean;
  onUpload: (type: ShippingReportImageType, files: File[]) => void;
  onDelete: (image: ShippingReportImage) => void;
  onCaptionChange: (imageId: number, caption: string) => void;
  onMove: (
    type: ShippingReportImageType,
    imageId: number,
    direction: -1 | 1,
  ) => void;
};

const formatEasternTime = (value: string) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Toronto",
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));

function CaptionInput({
  image,
  onSave,
  disabled,
}: {
  image: ShippingReportImage;
  onSave: (caption: string) => void;
  disabled?: boolean;
}) {
  const [caption, setCaption] = useState(image.caption ?? "");
  useEffect(() => setCaption(image.caption ?? ""), [image.caption]);

  return (
    <input
      value={caption}
      disabled={disabled}
      placeholder="Optional caption"
      className="w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      onChange={(event) => setCaption(event.target.value)}
      onBlur={() => {
        if (caption !== (image.caption ?? "")) onSave(caption);
      }}
    />
  );
}

export default function ShippingReportImageSection({
  title,
  description,
  type,
  images,
  uploading,
  disabled,
  onUpload,
  onDelete,
  onCaptionChange,
  onMove,
}: Props) {
  const cameraInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const orderedImages = [...images].sort(
    (a, b) => a.sort_order - b.sort_order || a.id - b.id,
  );

  const acceptFiles = (files: FileList | null) => {
    if (files?.length) onUpload(type, Array.from(files));
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={cameraInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            className="hidden"
            onChange={(event) => {
              acceptFiles(event.target.files);
              event.target.value = "";
            }}
          />
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={(event) => {
              acceptFiles(event.target.files);
              event.target.value = "";
            }}
          />
          <button
            type="button"
            disabled={uploading || disabled}
            onClick={() => cameraInput.current?.click()}
            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-60"
          >
            <CameraIcon className="h-5 w-5" /> Take Picture
          </button>
          <button
            type="button"
            disabled={uploading || disabled}
            onClick={() => fileInput.current?.click()}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
          >
            <PhotoIcon className="h-5 w-5" />
            {uploading ? "Uploading..." : "Choose Pictures"}
          </button>
        </div>
      </div>

      {orderedImages.length === 0 ? (
        <div className="mt-4 rounded-lg border-2 border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
          No pictures uploaded yet.
        </div>
      ) : (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {orderedImages.map((image, index) => (
            <article
              key={image.id}
              className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
            >
              <div className="flex h-52 items-center justify-center bg-slate-100">
                <img
                  src={image.url}
                  alt={image.caption || `${title} ${index + 1}`}
                  className="h-full w-full object-contain"
                />
              </div>
              <div className="space-y-3 p-3">
                {type === "temp_monitor" ? (
                  <p className="text-sm font-semibold text-slate-700">
                    Captured {formatEasternTime(image.created_at)}
                  </p>
                ) : null}
                <CaptionInput
                  image={image}
                  disabled={disabled}
                  onSave={(caption) => {
                    if (!disabled) onCaptionChange(image.id, caption);
                  }}
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">
                    Picture {index + 1}
                  </span>
                  <div className="flex gap-1">
                    <button type="button" disabled={disabled || index === 0} title="Move left" onClick={() => onMove(type, image.id, -1)} className="rounded p-1.5 text-slate-600 hover:bg-slate-200 disabled:opacity-30">
                      <ArrowLeftIcon className="h-4 w-4" />
                    </button>
                    <button type="button" disabled={disabled || index === orderedImages.length - 1} title="Move right" onClick={() => onMove(type, image.id, 1)} className="rounded p-1.5 text-slate-600 hover:bg-slate-200 disabled:opacity-30">
                      <ArrowRightIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      disabled={disabled}
                      title="Delete picture"
                      onClick={() => {
                        if (window.confirm("Delete this picture?")) onDelete(image);
                      }}
                      className="rounded p-1.5 text-rose-600 hover:bg-rose-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
